'use client';

import { get as _get } from 'lodash-es';
import { Fragment, useMemo } from 'react';
import { Form, Formik } from 'formik';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import { useQuery } from '@apollo/client/react';
import Main from '@/components/main';
import { useAppContext } from '@/contexts/app';
import { nearestCityQuery } from '@/apollo/auth';
import MaterialLink from '@/components/material-link';
import CalendarLinks from '@/components/calendar-links';
import RadioGroupField from '@/components/forms/radio-group-field';
import SectionHeader from '@/components/section-header';
import { Box } from '@mui/material';

export default function CalendarSubscribePageClient() {
  const { loggedInUser, isLoggedIn } = useAppContext();

  const {
    data: nearestCityData,
    loading: nearestCityLoading,
  } = useQuery(nearestCityQuery, {
    skip: isLoggedIn,
  });
  const nearestCity = _get(nearestCityData, 'nearestCity');

  const city = useMemo(() => {
    if (loggedInUser) {
      return loggedInUser.primaryCity;
    }
    return nearestCity;
  }, [loggedInUser, nearestCity]);

  return (
    <Main compact>
      <Typography variant="h1">Subscribe to Calendar</Typography>

      {nearestCityLoading && <p>Loading...</p>}

      <Formik
        initialValues={{
          calendarType: isLoggedIn ? 'ALL' : 'CITY',
        }}
        onSubmit={() => {}}
      >
        {({ values }) => {
          let path;
          if (values.calendarType === 'CITY') {
            path = `/cities/${_get(city, 'slug')}/calendar.ics`;
          } else {
            const query = values.calendarType === 'FOLLOWING' ? '?filter=following' : '';
            path = `/calendars/${loggedInUser.calendarToken}/calendar.ics${query}`;
          }

          return (
            <Fragment>
              <Form>
                <SectionHeader>Choose Your Feed</SectionHeader>
                <RadioGroupField
                  name="calendarType"
                  label="Include in Calendar Feed"
                  disabled={!isLoggedIn}
                  options={[
                    {
                      label: (
                        <Fragment>
                          City and Personalized (my groups and my following){' '}(<strong>recommended</strong>)
                        </Fragment>
                      ),
                      value: 'ALL',
                    },
                    {
                      label: 'Personalized (my groups and my following) Only',
                      value: 'FOLLOWING',
                    },
                    {
                      label: 'City Only',
                      value: 'CITY',
                    },
                  ]}
                />
                <Typography variant="body2" color="text.secondary">
                  Calendar links below include {_get(city, 'name')} events.
                </Typography>

                <Paper sx={{ p: 3, mt: 3, mb: 3 }}>
                  <Typography variant="h2" gutterBottom>Subscribe</Typography>
                  <Box sx={{ mb: 2 }}>
                    <CalendarLinks path={path} />
                  </Box>
                  <Typography variant="h3" gutterBottom>Good to Know</Typography>
                  <Stack spacing={2}>
                    {values.calendarType !== 'CITY' && (
                      <Alert severity="info">
                        RSVP &quot;Not Going&quot; to events you&apos;re not interested in to have them removed from your calendar subscription.
                      </Alert>
                    )}
                    <Typography variant="body2">
                      Group-only calendar feeds can be found at the bottom of each group&apos;s calendar page.
                    </Typography>
                    <Typography variant="body2">
                      Learn more about how calendar feeds work in the <MaterialLink href="/calendar-guide">Calendar Guide</MaterialLink>.
                    </Typography>
                    {loggedInUser && (
                      <Typography variant="body2">
                        Everyday Rides also sends weekly reminder emails that you can manage in your <MaterialLink href="/account">notification preferences</MaterialLink>.
                      </Typography>
                    )}
                    <Typography variant="body2">
                      Everyday Rides is free to use and funded by donations. Learn how to <MaterialLink href="/support?ref=subscribe">support Everyday Rides</MaterialLink>.
                    </Typography>
                  </Stack>
                </Paper>
              </Form>
            </Fragment>
          );
        }}
      </Formik>
    </Main>
  );
}
